import { useQuery } from '@apollo/client/react';

import { GET_USERS } from '@atom/graph/user';
import { Avatar, Progress } from '@atom/mui';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { getUserFullName, sortByFullName } from '@atom/utilities/userUtilities';

import classes from './adotView.module.css';

const MEMBERS_PAGE = 1;
const MEMBERS_LIMIT = 250;

const styles = {
  avatar: {
    height: 26,
    width: 26,
  },
};

type ADOTGroupMembersListProps = {
  groupId: string;
};

const ADOTGroupMembersList = ({ groupId }: ADOTGroupMembersListProps) => {
  const { data, loading } = useQuery(GET_USERS, {
    variables: {
      input: {
        userGroupId: groupId,
        page: MEMBERS_PAGE,
        limit: MEMBERS_LIMIT,
        showAdmin: hasRolePermissions(ROLE_SETS.ADMIN),
      },
    },
    skip: !groupId,
  });

  const members = [...(data?.users?.users || [])].sort(sortByFullName);

  const getContent = () => {
    if (loading) {
      return <Progress size={20} />;
    }

    if (members.length === 0) {
      return <div className={classes.memberEmpty}>No users in this group</div>;
    }

    return members.map(user => (
      <div
        key={user.id}
        className={classes.memberRow}
        data-cy={`adotGroupMember-${user.id}`}
      >
        <Avatar
          style={styles.avatar}
          src={user.photoUrl}
          alt={user.firstName}
        />
        <div>
          <div className={classes.memberName}>{getUserFullName(user)}</div>
          <div className={classes.memberSubtext}>
            {[user.email, user.title].filter(Boolean).join(' • ')}
          </div>
        </div>
      </div>
    ));
  };

  return (
    <div
      className={classes.membersList}
      data-cy={`adotGroupMembers-${groupId}`}
    >
      {getContent()}
    </div>
  );
};

export default ADOTGroupMembersList;
